import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Search, Heart, ShoppingBag, User, Menu, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";

function BrandMark({ settings, className = "" }) {
  if (settings?.logo_url) {
    return (
      <div className={`bg-white px-2.5 py-1 flex items-center ${className}`}>
        <img src={settings.logo_url} alt="Street Auto Accessories" className="h-8 md:h-9 w-auto" />
      </div>
    );
  }
  return (
    <div className={`flex items-baseline gap-2 whitespace-nowrap ${className}`}>
      <span className="font-display text-xl md:text-2xl tracking-tighter leading-none">
        {settings?.brand_name || "STREET AUTO"}
        <span className="text-[#E63946]">.</span>
      </span>
      <span className="hidden lg:inline overline text-white/60">{settings?.brand_suffix || "ACCESSORIES"}</span>
    </div>
  );
}

export default function Header() {
  const { settings, cartCount, wishlist, setCartOpen, categories, searchOpen, setSearchOpen } = useApp();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [q, setQ] = useState("");
  const [suggest, setSuggest] = useState([]);
  const debRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!q || q.length < 2) { setSuggest([]); return; }
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => {
      api.get("/search/suggest", { params: { q } }).then((r) => setSuggest(r.data.items || [])).catch(() => {});
    }, 200);
  }, [q]);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/shop", label: "Shop" },
    { to: "/collection/new-arrivals", label: "New Arrivals" },
    { to: "/collection/best-sellers", label: "Best Sellers" },
    { to: "/collection/deals", label: "Deals" },
    { to: "/ask-expert", label: "Ask an Expert" },
    { to: "/about", label: "About Us" },
    { to: "/contact", label: "Contact" },
  ];

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`sticky top-0 z-40 backdrop-blur-xl border-b transition-colors ${scrolled ? "bg-black/85 border-white/10" : "bg-black/60 border-white/5"}`}
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(true)}
            className="md:hidden p-2 -ml-2 text-white"
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>

          <Link to="/" data-testid="logo-link" className="flex items-center">
            <BrandMark settings={settings} />
          </Link>

          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="overline text-white/80 hover:text-white transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-1 md:gap-2">
            <button data-testid="search-toggle" onClick={() => setSearchOpen(true)} className="p-2 text-white hover:text-[#E63946] transition-colors" aria-label="Search">
              <Search size={20} />
            </button>
            <Link to="/wishlist" data-testid="wishlist-link" className="p-2 text-white hover:text-[#E63946] transition-colors relative" aria-label="Wishlist">
              <Heart size={20} />
              {wishlist.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#E63946] text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center">{wishlist.length}</span>
              )}
            </Link>
            <Link to="/account" data-testid="account-link" className="p-2 text-white hover:text-[#E63946] transition-colors hidden sm:inline-flex" aria-label="Customer account">
              <User size={20} />
            </Link>
            <button data-testid="cart-toggle" onClick={() => setCartOpen(true)} className="p-2 text-white hover:text-[#E63946] transition-colors relative" aria-label="Cart">
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-[#E63946] text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center">{cartCount}</span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[86%] max-w-sm bg-[#0A0A0A] border-r border-white/10 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <BrandMark settings={settings} />
              <button data-testid="mobile-menu-close" onClick={() => setMobileOpen(false)} className="p-2"><X size={22} /></button>
            </div>
            <nav className="flex-1 overflow-y-auto py-4">
              {navLinks.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="block px-6 py-3 text-white/90 hover:bg-white/5 border-b border-white/5 overline">
                  {l.label}
                </Link>
              ))}
              <div className="px-6 py-4 mt-2">
                <div className="overline text-white/40 mb-3">Shop by Category</div>
                {categories.map((c) => (
                  <Link key={c.slug} to={`/collection/${c.slug}`} onClick={() => setMobileOpen(false)} className="block py-2 text-sm text-white/80">
                    {c.name}
                  </Link>
                ))}
              </div>
            </nav>
            <div className="p-4 border-t border-white/10">
              <a href={`https://wa.me/91${settings?.whatsapp || ""}`} target="_blank" rel="noreferrer" className="sa-btn sa-btn-primary w-full">WhatsApp Us</a>
            </div>
          </div>
        </div>
      )}

      {/* Global search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md" data-testid="search-overlay">
          <div className="max-w-3xl mx-auto pt-24 px-4">
            <div className="flex items-center justify-between mb-6">
              <div className="overline text-white/50">SEARCH</div>
              <button onClick={() => setSearchOpen(false)} className="p-2 text-white"><X size={22} /></button>
            </div>
            <form onSubmit={submitSearch}>
              <input
                data-testid="search-input"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full bg-transparent border-b border-white/30 text-2xl md:text-4xl font-display py-4 focus:outline-none focus:border-[#E63946]"
                placeholder="Search for accessories, LED, mats, phone holders..."
              />
            </form>
            {suggest.length > 0 && (
              <div className="mt-6 divide-y divide-white/5 border-t border-white/5">
                {suggest.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/product/${p.slug}`}
                    onClick={() => setSearchOpen(false)}
                    className="flex items-center gap-4 py-3 hover:bg-white/5 px-2"
                    data-testid={`search-suggestion-${p.slug}`}
                  >
                    <div className="w-12 h-12 bg-white/5 flex-shrink-0 overflow-hidden">
                      {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm">{p.name}</div>
                    </div>
                    <div className="text-[#E63946] font-bold">₹{p.price?.toLocaleString("en-IN")}</div>
                  </Link>
                ))}
              </div>
            )}
            {q.length >= 2 && suggest.length === 0 && (
              <div className="mt-8 text-center py-8 border border-white/10">
                <div className="overline text-white/50 mb-2">No matches yet</div>
                <div className="font-display text-2xl mb-3">Can't find what you're looking for?</div>
                <Link to="/ask-expert" onClick={() => setSearchOpen(false)} className="sa-btn sa-btn-primary inline-flex">ASK AN EXPERT</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
